import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import Image from "next/image"
import { useEffect, useState } from "react"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { CustomImage } from "ui"
import { transitionEase, transitionTime } from "ui/transitions/utils"
import { HistoryData, versionedAsset } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const Layout = styled.div`
  display: none;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    flex-direction: column;
    gap: 54px;
    padding-left: max(88px, calc((100vw - 1280px) / 2 + 88px));
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    padding-left: max(200px, calc((100vw - 1920px) / 2 + 200px));
  }
  width: 100%;
  text-align: center;
  overflow: visible;
`

const SwiperContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  .swiper {
    overflow: visible;
  }
`
const StyledSwiperSlide = styled(SwiperSlide)`
  width: 184px !important;
  margin-right: 112px;
  ${({ theme }) => theme.breakpoints.up("xl")} {
    margin-right: 200px;
  }
`

const StyledPin = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 200px;
  border: 3px solid white;
  background-color: #1bbd77;
  box-shadow: 0px 0px 16px 0px rgba(27, 189, 119, 0.8);
`

const StyledElement = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "$inView",
})<{ $inView: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 56px;
  opacity: ${(props) => (props.$inView ? 1 : 0)};
  transition: all ${transitionTime}s ${transitionEase};
`

const StyledImage = styled(CustomImage)`
  border-radius: 12px;
  overflow: hidden;
`

const TimelineLine = styled(Image)`
  position: absolute;
  width: calc(100% - 112px);
  height: 2px;
  left: 0;
  top: 238px;
  z-index: 1;

  ${({ theme }) => theme.breakpoints.up("xl")} {
    width: calc(100% - 200px);
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`

const HistoryBus = styled(Image, {
  shouldForwardProp: (prop: string) => prop !== "isVisible",
})<{ isVisible: boolean }>`
  position: absolute;
  left: 0;
  top: 200px;
  z-index: 10;
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  animation: ${(props) =>
    props.isVisible ? "moveBus 5s linear forwards" : "none"};

  @keyframes moveBus {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    99% {
      transform: translateX(100vw);
      opacity: 1;
    }
    100% {
      transform: translateX(100vw);
      opacity: 0;
      visibility: hidden;
    }
  }
`

export default function HistorySwiper({
  data,
  inView,
  locale,
}: {
  data: HistoryData[]
  inView: boolean
  locale: string
}) {
  const [animatingItems, setAnimatingItems] = useState<boolean[]>([])
  useEffect(() => {
    if (inView) {
      setAnimatingItems(new Array(data.length).fill(false))
      const timers: NodeJS.Timeout[] = []
      data.forEach((_, index) => {
        const delay = (index - 0.2) * 1000
        const timer = setTimeout(() => {
          setAnimatingItems((prev) => {
            const newState = [...prev]
            newState[index] = true
            return newState
          })
        }, delay)
        timers.push(timer)
      })
      return () => {
        timers.forEach((timer) => clearTimeout(timer))
      }
    }
  }, [inView, data.length])
  const isReverse = locale === "en" || locale === "ru"
  return (
    <Layout>
      <SwiperContainer>
        <TimelineLine
          alt=""
          src={"/images/historyline.png"}
          width={1920}
          height={2}
        />
        <HistoryBus
          isVisible={inView}
          alt="History Bus"
          src={versionedAsset(
            isReverse
              ? "/images/historyBusMirror.svg"
              : "/images/historyBus.png",
          )}
          width={180}
          height={68}
        />

        <Swiper initialSlide={0} slidesPerView={"auto"}>
          {data.map((el, i) => (
            <StyledSwiperSlide key={i}>
              <StyledElement $inView={animatingItems[i]}>
                <StyledImage
                  src={getUmbracoMediaUrl(el.desktopImage)}
                  alt={el.title || "History Image"}
                  width={184}
                  height={173}
                  style={{ objectFit: "cover" }}
                />
                <StyledPin />
                <TextBox>
                  <Typography variant="h3">
                    {el.yearForHomepage
                      ? new Date(el.yearForHomepage).getFullYear()
                      : el.years}
                  </Typography>
                  <Typography variant="body1">{el.subtitle}</Typography>
                </TextBox>
              </StyledElement>
            </StyledSwiperSlide>
          ))}
        </Swiper>
      </SwiperContainer>
    </Layout>
  )
}
